import React from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  CodeBracketIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline';
import {
  ItemLink,
  ItemIcon,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemAction,
} from '@dashboard/components/ui/item';

export const FRAMER_PLUGIN_URL = 'https://www.framer.com/marketplace/plugins/checkout-page/';

const EMBED_DESCRIPTION_MAP = {
  event: 'Embed the event on your own site or landing page',
  checkout: 'Embed the checkout on your own site or landing page',
  form: 'Embed the form on your own site or landing page',
};

const FRAMER_DESCRIPTION_MAP = {
  event: 'Use our plugin to add the event to your Framer site',
  checkout: 'Use our plugin to add the checkout to your Framer site',
  form: 'Use our plugin to add the form to your Framer site',
};

const QR_DESCRIPTION_MAP = {
  event: 'Scan to open event',
  checkout: 'Scan to open checkout page',
  form: 'Scan to open form',
};

const cardClassName =
  'w-full rounded-lg border border-gray-200 dark:border-border p-4 min-h-[80px] outline-none focus-visible:ring-2 focus-visible:ring-gray-950 dark:focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-gray-100 dark:active:bg-muted';

function FramerIcon() {
  return (
    <svg viewBox="0 0 75 112" fill="currentColor" aria-hidden="true">
      <path d="M0 36.77h36.773l37.619 37.615H36.773V112L0 74.385V36.77ZM0 0h74.392v36.77H36.773L0 0Z" />
    </svg>
  );
}

function ActionCardBody({ icon, title, description, action }) {
  return (
    <>
      <ItemIcon className="[&>svg]:size-6 size-6 text-gray-600 dark:text-muted-foreground self-center">
        {icon}
      </ItemIcon>
      <ItemContent>
        <ItemTitle className="font-semibold text-gray-900 dark:text-foreground">{title}</ItemTitle>
        <ItemDescription className="text-sm text-gray-500 dark:text-muted-foreground whitespace-normal">
          {description}
        </ItemDescription>
      </ItemContent>
      <ItemAction>{action}</ItemAction>
    </>
  );
}

function ActionCard({ onClick, icon, title, description }) {
  return (
    <ItemLink asChild onClick={onClick} className={cardClassName}>
      <button type="button">
        <ActionCardBody
          icon={icon}
          title={title}
          description={description}
          action={<ChevronRightIcon className="h-5 w-5" />}
        />
      </button>
    </ItemLink>
  );
}

function ExternalActionCard({ href, icon, title, description }) {
  return (
    <ItemLink href={href} target="_blank" rel="noreferrer" className={cardClassName}>
      <ActionCardBody
        icon={icon}
        title={title}
        description={description}
        action={<ArrowTopRightOnSquareIcon className="h-5 w-5" />}
      />
    </ItemLink>
  );
}

const ShareActionCards = ({ page, onAddToWebsite, onQRCode }) => {
  const embedDescription = EMBED_DESCRIPTION_MAP[page.type] || EMBED_DESCRIPTION_MAP.checkout;
  const framerDescription = FRAMER_DESCRIPTION_MAP[page.type] || FRAMER_DESCRIPTION_MAP.checkout;
  const qrDescription = QR_DESCRIPTION_MAP[page.type] || QR_DESCRIPTION_MAP.checkout;

  return (
    <div className="flex flex-col gap-3">
      <ActionCard
        onClick={onAddToWebsite}
        icon={<CodeBracketIcon />}
        title="Add to website"
        description={embedDescription}
      />
      <ExternalActionCard
        href={FRAMER_PLUGIN_URL}
        icon={<FramerIcon />}
        title="Add to Framer"
        description={framerDescription}
      />
      <ActionCard
        onClick={onQRCode}
        icon={<QrCodeIcon />}
        title="QR code"
        description={qrDescription}
      />
    </div>
  );
};

export default ShareActionCards;
